// src/contexts/AuthContext.js
import { createContext, useState, useContext, useEffect } from 'react';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import validateTokens from '../services/TokenChecker';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState({});
  const [isValidToken, setIsValidToken] = useState(false);

  // Permission and location states
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [hasCameraPermission, setHasCameraPermission] = useState(false);
  const [hasLocationPermission, setHasLocationPermission] = useState(false);
  const [hasMicrophonePermission, setHasMicrophonePermission] = useState(false); // ✅ NEW

  const access_token = Cookies.get('access_token');
  const refresh_token = Cookies.get('refresh_token');

  // Location request logic
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setHasLocationPermission(true);
        },
        (error) => {
          setLocationError(error.message);
          setHasLocationPermission(false);
        }
      );
    } else {
      setLocationError('Geolocation is not supported');
      setHasLocationPermission(false);
    }
  }, []);

  // Camera request logic
  useEffect(() => {
    const requestCameraAccess = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        setHasCameraPermission(true);
        stream.getTracks().forEach(track => track.stop());
      } catch (error) {
        setHasCameraPermission(false);
      }
    };

    if (!hasCameraPermission) {
      requestCameraAccess();
    }
  }, [hasCameraPermission]);

  // ✅ Microphone request logic
  useEffect(() => {
    const requestMicrophoneAccess = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setHasMicrophonePermission(true);
        stream.getTracks().forEach(track => track.stop());
      } catch (error) {
        setHasMicrophonePermission(false);
      }
    };

    if (!hasMicrophonePermission) {
      requestMicrophoneAccess();
    }
  }, [hasMicrophonePermission]);

  // Decode and set user if tokens exist
  useEffect(() => {
    if (access_token && refresh_token) {
      setIsLoggedIn(true);
      const decodedToken = jwtDecode(access_token);
      const userData = {
        first_name: decodedToken.first_name,
        middle_name: decodedToken.middle_name,
        last_name: decodedToken.last_name,
        user_id: decodedToken.user_id,
        user_profile_photo: decodedToken.user_profile_photo,
        email: decodedToken.email,
        mobile_number: decodedToken.mobile_number,
        user_type: decodedToken.user_type,
        signature: decodedToken.signature,
        enp_roll_no: decodedToken.enp_roll_no,
        enp_commission_no: decodedToken.enp_commission_no,
        enp_commission_expiry_date: decodedToken.enp_commission_expiry_date,
        enp_ptr_no: decodedToken.enp_ptr_no,
        enp_ptr_issued_place: decodedToken.enp_ptr_issued_place,
        enp_ptr_issued_date: decodedToken.enp_ptr_issued_date,
        enp_ibp_no: decodedToken.enp_ibp_no,
        enp_email: decodedToken.enp_email,
        enp_address: decodedToken.enp_address,
        enp_compliance_no: decodedToken.enp_compliance_no,
        enp_compliance_issued_date: decodedToken.enp_compliance_issued_date,
        enp_notary_name: decodedToken.enp_notary_name,
      };

      setUser(userData);
    } else {
      setIsLoggedIn(false);
    }
    setIsLoading(false);
  }, [access_token, refresh_token]);

  const FIVE_MINUTES = 5 * 60 * 1000;
  const [lastTokenCheck, setLastTokenCheck] = useState(null);

  const checkTokensValidity = async () => {
    const now = Date.now();

    if (lastTokenCheck && now - lastTokenCheck < FIVE_MINUTES) {
      console.log("Skipping token check (recently validated)");
      return;
    }

    const isValid = await validateTokens(access_token, refresh_token);
    setLastTokenCheck(now);

    if (isValid) {
      setIsValidToken(true);
    } else {
      Cookies.remove('access_token');
      Cookies.remove('refresh_token');
      setIsValidToken(false);
      setIsLoggedIn(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn && !isValidToken) {
      const interval = setInterval(() => {
        checkTokensValidity();
      }, FIVE_MINUTES);

      return () => clearInterval(interval);
    }
  }, [isLoggedIn, isValidToken, access_token, refresh_token, lastTokenCheck]);

  const login = (response) => {
    const { access_token, refresh_token, user_details } = response;

    Cookies.set('access_token', access_token, { sameSite: 'Strict' });
    Cookies.set('refresh_token', refresh_token, { sameSite: 'Strict' });

    localStorage.setItem('lawyer_profile_photo_base64', user_details.profile_photo_base64);
    localStorage.setItem('lawyer_signature_base64', user_details.signature_base64);

    const decodedToken = jwtDecode(access_token);
    const userData = {
      first_name: decodedToken.first_name,
      middle_name: decodedToken.middle_name,
      last_name: decodedToken.last_name,
      user_id: decodedToken.user_id,
      user_profile_photo: decodedToken.user_profile_photo,
      email: decodedToken.email,
      mobile_number: decodedToken.mobile_number,
      user_type: decodedToken.user_type,
      signature: decodedToken.signature,
      enp_roll_no: decodedToken.enp_roll_no,
      enp_commission_no: decodedToken.enp_commission_no,
      enp_commission_expiry_date: decodedToken.enp_commission_expiry_date,
      enp_ptr_no: decodedToken.enp_ptr_no,
      enp_ptr_issued_place: decodedToken.enp_ptr_issued_place,
      enp_ptr_issued_date: decodedToken.enp_ptr_issued_date,
      enp_ibp_no: decodedToken.enp_ibp_no,
      enp_email: decodedToken.enp_email,
      enp_address: decodedToken.enp_address,
      enp_compliance_no: decodedToken.enp_compliance_no,
      enp_compliance_issued_date: decodedToken.enp_compliance_issued_date,
      enp_notary_name: decodedToken.enp_notary_name,
    };

    setUser(userData);
    setIsLoggedIn(true);
  };

  const logout = () => {
    setIsLoggedIn(false);
    setUser({});
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    localStorage.clear();
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isLoading,
        user,
        login,
        logout,
        location,
        locationError,
        hasCameraPermission,
        hasLocationPermission,
        hasMicrophonePermission, // ✅ Exposed to context
        setHasCameraPermission,
        setHasLocationPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
